/**
 * Collection of `PaneConfig` instances forming a multi-viewport layout.
 *
 * Responsibilities:
 *   - Attach panes to the viewer (creating camera/view/controls per pane).
 *   - Hit-test a pixel coordinate to find which pane the cursor is over.
 *   - Dispose pane resources on layout teardown.
 *
 * Lifecycle: created by `viewer.setMultiViewLayout(panes)`, torn down by
 * `viewer.clearMultiViewLayout()`.
 */

export class MultiViewLayout {
  /**
   * @param {object} viewer
   * @param {import('./PaneConfig.js').PaneConfig[]} panes
   */
  constructor(viewer, panes) {
    this.viewer = viewer;
    this.panes = panes;
    for (const pane of this.panes) {
      pane.attach(viewer);
      // Bind controls to the real scene (Proxied internally to the pane's view).
      pane.controls.setScene(viewer.scene);
    }
  }

  /**
   * Hit-test which pane the canvas-local pixel (px, py) is in.
   * Coordinates are in WebGL convention (origin bottom-left).
   * Returns null if the point is outside all panes.
   */
  paneAtPixel(px, py, canvasW, canvasH) {
    for (const p of this.panes) {
      const vp = p.pixelViewport(canvasW, canvasH);
      if (px >= vp.x && px < vp.x + vp.w && py >= vp.y && py < vp.y + vp.h) {
        return p;
      }
    }
    return null;
  }

  getPaneById(id) {
    return this.panes.find((p) => p.id === id) ?? null;
  }

  dispose() {
    for (const p of this.panes) {
      // Stop any in-flight tweens / decelerations
      if (p.controls && typeof p.controls.stop === 'function') p.controls.stop();
      p.detach();
    }
    this.panes = [];
  }
}
